import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl, money, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { ArrowLeft, Truck, ShoppingBag, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setProduct(null); setNotFound(false); setActive(0); setSize(""); setQty(1);
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-serif text-3xl text-kys-ink">Piece not found</h1>
      <Link to="/shop" className="mt-4 inline-flex text-sm font-semibold text-kys-accent">← Back to shop</Link>
    </div>
  );
  if (!product) return <div className="mx-auto max-w-6xl px-4 py-16"><div className="aspect-[3/4] max-w-md animate-pulse rounded-md bg-kys-blush-soft" /></div>;

  const soldOut = product.sold_out;
  const needsSize = (product.sizes || []).length > 0;
  const maxQty = product.track_inventory ? product.inventory : 99;

  const addToBag = () => {
    if (soldOut) return;
    if (needsSize && !size) { toast.error("Please select a size"); return; }
    add(product, size, qty);
    toast.success("Added to your bag");
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-1.5 text-sm text-kys-charcoal hover:text-kys-accent">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-14">
        {/* Gallery */}
        <div>
          <div className="aspect-[3/4] overflow-hidden rounded-lg border border-kys-blush-border bg-kys-blush">
            {product.images?.[active] ? (
              <img src={fileUrl(product.images[active])} alt={product.images[active].alt || product.name} className="h-full w-full object-cover" />
            ) : <div className="flex h-full w-full items-center justify-center text-kys-muted">No photo</div>}
          </div>
          {product.images?.length > 1 && (
            <div className="mt-3 flex gap-3">
              {product.images.map((im, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                  className={`h-20 w-16 overflow-hidden rounded border ${active === i ? "border-kys-accent" : "border-kys-blush-border"}`}>
                  <img src={fileUrl(im)} alt={im.alt || ""} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <h1 className="font-serif text-3xl tracking-tight text-kys-ink sm:text-4xl">{product.name}</h1>
          <p className="mt-3 font-mono text-xl text-kys-charcoal">{money(product.price)}</p>
          {soldOut && <p data-testid="sold-out-label" className="mt-3 inline-block rounded-full bg-kys-ink px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">Sold Out</p>}

          {product.description && <p className="mt-5 text-sm leading-relaxed text-kys-charcoal">{product.description}</p>}

          {needsSize && (
            <div className="mt-6">
              <p className="label-accent mb-2 text-kys-muted">Size</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)}
                    className={`min-w-[3rem] rounded border px-3 py-2 text-sm font-medium transition ${size === s ? "border-kys-ink bg-kys-ink text-white" : "border-kys-blush-border bg-white text-kys-charcoal hover:border-kys-accent"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.measurements && (
            <div className="mt-6 rounded-md border border-kys-blush-border bg-white p-4">
              <p className="label-accent mb-1 text-kys-muted">Measurements</p>
              <p className="whitespace-pre-line text-sm text-kys-charcoal">{product.measurements}</p>
            </div>
          )}

          {!soldOut && (
            <div className="mt-6 flex items-center gap-4">
              <div className="flex items-center rounded border border-kys-blush-border">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2.5" aria-label="Decrease"><Minus className="h-4 w-4" /></button>
                <span data-testid="qty-value" className="w-8 text-center text-sm font-medium">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(maxQty, q + 1))} className="px-3 py-2.5" aria-label="Increase"><Plus className="h-4 w-4" /></button>
              </div>
              {product.track_inventory && <span className="text-xs text-kys-muted">{product.inventory} available</span>}
            </div>
          )}

          <button data-testid="add-to-bag-btn" onClick={addToBag} disabled={soldOut}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-kys-ink py-4 text-sm font-semibold text-white transition hover:bg-kys-accent disabled:cursor-not-allowed disabled:bg-kys-muted">
            <ShoppingBag className="h-4 w-4" /> {soldOut ? "Sold Out" : "Add to Bag"}
          </button>

          <div className="mt-4 flex items-start gap-2 text-xs text-kys-muted">
            <Truck className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{product.shipping_info || "U.S. shipping only. Shipping calculated at checkout once configured."}</span>
          </div>
        </div>
      </div>

      {product.related?.length > 0 && (
        <div className="mt-20">
          <h2 className="mb-6 font-serif text-2xl text-kys-ink sm:text-3xl">You may also like</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-8">
            {product.related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
